import * as React from 'react';
import classnames from 'classnames';

import DeliverableMediaAttachment from './DeliverableMediaAttachment';
import DeliverableMediaBlank from './DeliverableMediaBlank';
import DeliverableMediaRowChild from './row-child/DeliverableMediaRowChild';
import { withModal } from 'components/modal/ModalHOC';
import DroppableAttachmentElement from 'modules/admin/components/deliverables/dnd/DroppableAttachmentElement';
import Tooltip from 'components/Tooltip';

import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  prepareDeliverableAttachmentsForGallery
} from 'utils/deliverables';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';

import type {
  DeliverableAttachment,
  DeliverableAttachmentType,
  GalleryModalItem
} from 'types';

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

applyTranslations(require('./deliverable-media-attachments-texts.i18n.json'));

type Props = {
  deliverableAttachmentType?: DeliverableAttachmentType,
  mediaWidth?: number,
  mediaHeight?: number,
  canDragAndDrop?: boolean,
  canUpload: boolean,
  renderCustomAsset?: (item: GalleryModalItem, index: number) => React.ReactNode,
  showFileName?: boolean,
  placeholderForUpload?: React.ReactNode | null,
  compactDisplay?: boolean,
  attachments: Array<DeliverableAttachment>,
  isImagePreviewEnabled?: boolean,
  useAssetDirectly?: boolean,
  onDeleteAttachment?: (attachment: DeliverableAttachment) => void
};

class DeliverableMediaAttachments extends React.PureComponent<Props> {
  render(): React.ReactNode {
    const {
      deliverableAttachmentType,
      canDragAndDrop,
      renderCustomAsset,
      attachments,
      canUpload,
      showFileName,
      placeholderForUpload,
      isImagePreviewEnabled,
      onDeleteAttachment,
      compactDisplay,
      mediaWidth,
      mediaHeight,
      useAssetDirectly
    } = this.props;

    let attachmentsToDisplay = attachments;

    if (!canUpload && attachmentsToDisplay.length === 0) {
      const BlankComponent = deliverableAttachmentType === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS
        ? (
          <Tooltip
            width="long"
            message={__t('deliverable_media_attachments_texts.tooltip')}
          >
            <DeliverableMediaBlank />
          </Tooltip>
        ) : <DeliverableMediaBlank />;

      return (
        <DeliverableMediaRowChild>
          {BlankComponent}
        </DeliverableMediaRowChild>
      );
    }

    const galleryItems = prepareDeliverableAttachmentsForGallery(attachmentsToDisplay, useAssetDirectly);

    if (compactDisplay) {
      attachmentsToDisplay = attachmentsToDisplay.slice(0, 1);
    }

    const wrapperClassNames = classnames('deliverable-media-row__attachments', {
      'deliverable-media-row__attachments--stacked': attachments.length > 1 && compactDisplay,
      [`deliverable-media-row__attachments--stacked--small-${mediaWidth || ''}-${mediaHeight || ''}`]:
        attachments.length > 1 && compactDisplay && mediaHeight && mediaWidth
    });

    return (
      <div className={wrapperClassNames}>
        {
          attachmentsToDisplay.map((attachment, index) => {
            const attachmentComponent = (
              <DivWithModal
                key={`attachment-${attachment.id}`}
                modalContentProps={{
                  renderCustomAsset,
                  items: galleryItems,
                  currentPosition: index
                }}
              >
                <DeliverableMediaAttachment
                  width={mediaWidth}
                  height={mediaHeight}
                  showFileName={showFileName}
                  attachment={attachment}
                  isImagePreviewEnabled={isImagePreviewEnabled}
                  useAssetDirectly={useAssetDirectly}
                  onDeleteAttachment={canUpload ? onDeleteAttachment : undefined}
                />
              </DivWithModal>
            );

            if (!canDragAndDrop) {
              return attachmentComponent;
            }

            return (
              <DroppableAttachmentElement
                key={`attachment-droppable-${attachment.id}`}
                id={attachment.id}
                index={index}
              >
                {attachmentComponent}
              </DroppableAttachmentElement>
            );
          })
        }
        {canUpload && placeholderForUpload}
      </div>
    );
  }
}

export default DeliverableMediaAttachments;
